import React from "react";
import { css } from "@emotion/css";

// Format ISO date or "YYYY-MM-DD HH:mm:ss" for display
function formatPassDate(dateStr) {
    if (!dateStr) return "";
    try {
        const d = new Date(dateStr.replace(" ", "T"));
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return dateStr;
    }
}

function formatPassTime(timeStr) {
    if (!timeStr) return "";
    try {
        const t = timeStr.length <= 8 ? `1970-01-01T${timeStr}Z` : timeStr.replace(" ", "T");
        const d = new Date(t);
        if (isNaN(d.getTime())) return timeStr;
        return d.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    } catch {
        return timeStr;
    }
}

function formatValidUntil(isoStr) {
    if (!isoStr) return "";
    try {
        const d = new Date(isoStr);
        if (isNaN(d.getTime())) return isoStr;
        return d.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    } catch {
        return isoStr;
    }
}

function formatCheckInTime(isoStr) {
    if (!isoStr) return "";
    try {
        const d = new Date(isoStr);
        if (isNaN(d.getTime())) return isoStr;
        return d.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    } catch {
        return isoStr;
    }
}

/* Theme: gold #f5b940, purple #49255d, dashboard bg #f4f4f9 */
const passCardContainerStyle = css`
    margin-top: 12px;
    padding: 12px 16px;
    background: linear-gradient(180deg, #f4f4f9 0%, #ebe8f0 100%);
    border-top: 1px solid #e8e4ef;
`;

const passCardStyle = css`
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 4px 14px rgba(73, 37, 93, 0.08);
    border: 1px solid #e8e4ef;
    border-left: 4px solid #f5b940;
`;

const passHeaderStyle = css`
    padding: 18px 20px 14px;
    border-bottom: 1px solid #ede9f5;
    flex-shrink: 0;
`;

const passTitleStyle = css`
    font-size: 18px;
    font-weight: 700;
    color: #49255d;
    margin: 0 0 10px 0;
    letter-spacing: -0.02em;
`;

const passHeaderRowStyle = css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const passSubtitleStyle = css`
    font-size: 13px;
    color: #5a5266;
    margin: 0;
    line-height: 1.45;
    flex: 1;
    min-width: 0;
`;

const refreshButtonStyle = css`
    padding: 8px 16px;
    border-radius: 8px;
    border: none;
    background-color: #f5b940;
    color: #1f1f1f;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: background-color 0.15s, opacity 0.15s;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        background-color: #dca332;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const passBodyStyle = css`
    display: grid;
    grid-template-columns: 1fr;
    min-width: 0;
    @media (min-width: 640px) {
        grid-template-columns: 1fr 220px;
        align-items: start;
    }
`;

const passDetailsStyle = css`
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px 24px;
    padding: 18px 20px;
    font-size: 13px;
    color: #5a5266;
    min-width: 0;
    @media (min-width: 480px) {
        grid-template-columns: 100px 1fr;
    }
`;

const passDetailRowStyle = css`
    display: contents;
`;

const passLabelStyle = css`
    font-weight: 600;
    color: #5a5266;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding-top: 1px;
`;

const passValueStyle = css`
    color: #49255d;
    font-weight: 500;
    word-break: break-word;
`;

const attendanceRowStyle = css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
`;

const attendanceBadgeStyle = (attended) => css`
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    background-color: ${attended ? "#dcfce7" : "#fef3c7"};
    color: ${attended ? "#166534" : "#b45309"};
`;

const checkInTimeStyle = css`
    font-size: 12px;
    color: #7a7289;
    font-weight: 500;
`;

const validUntilStyle = css`
    font-size: 11px;
    color: #7a7289;
    padding: 12px 20px;
    border-top: 1px dashed #e8e4ef;
    grid-column: 1 / -1;
`;

const qrSectionStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    border-top: 1px solid #ede9f5;
    gap: 12px;
    flex-shrink: 0;
    @media (min-width: 640px) {
        border-top: none;
        border-left: 1px solid #ede9f5;
        width: 220px;
        min-width: 220px;
        max-width: 220px;
        box-sizing: border-box;
    }
`;

const qrWrapperStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 14px;
    background-color: #ffffff;
    border-radius: 12px;
    border: 2px dashed #e0d8eb;
    box-shadow: 0 1px 3px rgba(73, 37, 93, 0.06);
    flex-shrink: 0;
`;

const qrCaptionStyle = css`
    font-size: 11px;
    color: #5a5266;
    font-weight: 500;
    text-align: center;
`;

const EventQrPassCard = ({ profile, event, passData, onRefresh, isRefreshing }) => {
    if (!passData) return null;

    const displayName = profile?.full_name || profile?.name || "N/A";
    const eventTitle = passData.event?.title || event?.title;
    const eventDateRaw = passData.event?.date || event?.start_date;
    const eventTimeRaw = event?.start_time;
    const eventDateFormatted = formatPassDate(eventDateRaw);
    const eventTimeFormatted = formatPassTime(eventTimeRaw);
    const collegeName = profile?.college_name;
    const marked = passData.attendance_status?.marked;
    const checkInTime = passData.attendance_status?.check_in_time;
    const validUntil = passData.valid_until;
    const qrCode = passData.qr_code;

    const checkInFormatted = checkInTime ? formatCheckInTime(checkInTime) : "";

    return (
        <div className={passCardContainerStyle}>
            <div className={passCardStyle}>
                <header className={passHeaderStyle}>
                    <h3 className={passTitleStyle}>Event QR Pass</h3>
                    <div className={passHeaderRowStyle}>
                        <p className={passSubtitleStyle}>
                            Please present this pass at the venue for check-in.
                        </p>
                        {onRefresh && (
                            <button
                                className={refreshButtonStyle}
                                onClick={onRefresh}
                                disabled={isRefreshing}
                            >
                                {isRefreshing ? "Refreshing…" : "Refresh Pass"}
                            </button>
                        )}
                    </div>
                </header>

                <div className={passBodyStyle}>
                    <div className={passDetailsStyle}>
                        <div className={passDetailRowStyle}>
                            <span className={passLabelStyle}>Name</span>
                            <span className={passValueStyle}>{displayName}</span>
                        </div>
                        <div className={passDetailRowStyle}>
                            <span className={passLabelStyle}>Event</span>
                            <span className={passValueStyle}>{eventTitle}</span>
                        </div>
                        <div className={passDetailRowStyle}>
                            <span className={passLabelStyle}>Date & time</span>
                            <span className={passValueStyle}>
                                {eventDateFormatted}
                                {eventTimeFormatted ? ` at ${eventTimeFormatted}` : ""}
                            </span>
                        </div>
                        {collegeName && (
                            <div className={passDetailRowStyle}>
                                <span className={passLabelStyle}>College</span>
                                <span className={passValueStyle}>{collegeName}</span>
                            </div>
                        )}
                        <div className={passDetailRowStyle}>
                            <span className={passLabelStyle}>Attendance</span>
                            <span className={attendanceRowStyle}>
                                <span className={attendanceBadgeStyle(marked)}>
                                    {marked ? "Attended" : "Not yet attended"}
                                </span>
                                {checkInFormatted && (
                                    <span className={checkInTimeStyle}>
                                        {checkInFormatted}
                                    </span>
                                )}
                            </span>
                        </div>
                    </div>

                    {qrCode && (
                        <div className={qrSectionStyle}>
                            <div className={qrWrapperStyle}>
                                <img
                                    src={qrCode}
                                    alt="Event QR Pass"
                                    style={{ width: "160px", height: "160px", objectFit: "contain" }}
                                />
                            </div>
                            <span className={qrCaptionStyle}>Scan at venue</span>
                        </div>
                    )}

                    {validUntil && (
                        <div className={validUntilStyle}>
                            Valid until {formatValidUntil(validUntil)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EventQrPassCard;

